import React, { FunctionComponent, useCallback, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { getCurrentPaintingScrollPosition, getScaleIndex } from "@multiweb/picasso";
import { AlignItems, ButtonBackgroundColor, IconButton, IconSelectItem, Layout, Select, SelectBody } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { doNothing } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { createUpdateWorkflowViewAction } from "../../actions";
import { LOC_ADD, LOC_COMMON, LOC_EXISTING_INTO_THE_SET, LOC_FINAL, LOC_STARTING } from "../../locKeys";
import { getPicassoState, getSelectedSetId } from "../../selectors";
import { WorkflowEditorProps } from "../../types";
import { UiWorkflow } from "../../UiWorkflow";
import { WorkflowManager } from "../../WorkflowManager";
import { Activity, ActivityType, getActivityNewPosition, Position } from "../../workflowModel";
import { ExistingActivitiesDialog } from "../dialogs/basicDialogs/ExistingActivitiesDialog";

type AddActivityDropdownSelectComponentProps = {
    readonly uiWorkflow: UiWorkflow;
    readonly selectedSetId: string;
    readonly scaleIndex: number;
    readonly updateWorkflowView: (setId: string, selectedActivityId?: string, selectedTransitionId?: string) => void;
    readonly addText: string;
    readonly startingText: string;
    readonly commonText: string;
    readonly existingIntoTheSetText: string;
    readonly finalText: string;
} & WorkflowEditorProps;

const AddActivityDropdownSelectComponent: FunctionComponent<AddActivityDropdownSelectComponentProps> = ({
    hostKey,
    uiWorkflow,
    selectedSetId,
    scaleIndex,
    updateWorkflowView,
    addText,
    startingText,
    commonText,
    existingIntoTheSetText,
    finalText,
}) => {
    const [showExistingActivitiesDiag, setShowExistingActivitiesDiag] = useState<boolean>(false);

    const getCurrentPicassoScrollPosition = useCallback(() => {
        const editorElementRef = WorkflowManager.uiWorkflows[hostKey].editorElement;
        return getCurrentPaintingScrollPosition(editorElementRef);
    }, [hostKey]);

    const addNewActivity = (activityType: ActivityType) => {
        const picassoScrollPosition = getCurrentPicassoScrollPosition();
        const newPosition = getActivityNewPosition(picassoScrollPosition.left, picassoScrollPosition.top, scaleIndex);
        const activityId = uiWorkflow.workflow.addActivity(activityType, selectedSetId, newPosition.x, newPosition.y);
        updateWorkflowView(selectedSetId, activityId);
    };

    const addExistingActivities = (activitiesToAdd: Activity[]) => {
        const picassoScrollPosition = getCurrentPicassoScrollPosition();

        activitiesToAdd.forEach(activityToAdd => {
            const newPosition = getActivityNewPosition(picassoScrollPosition.left, picassoScrollPosition.top, scaleIndex);
            activityToAdd.positions.push(new Position(newPosition.x, newPosition.y, activityToAdd.type === ActivityType.normal ? 100 : 60, 60, uiWorkflow.uiSet.set));
        });

        updateWorkflowView(selectedSetId, activitiesToAdd[activitiesToAdd.length - 1].id);
    };

    return (
        <Layout alignItems={AlignItems.center}>
            <Select
                value={undefined}
                onChange={doNothing}
                options={[
                    { text: startingText, value: ActivityType.start },
                    { text: commonText, value: ActivityType.normal },
                    { text: existingIntoTheSetText, value: "existujici" },
                    { text: finalText, value: ActivityType.end },
                ]}
                optionsContent={({ opened, onSelect }) => {
                    const canCreateStartActivity = !uiWorkflow.workflow.isAnyActivtyOfStartType;
                    return (opened && <SelectBody>
                        {canCreateStartActivity && <IconSelectItem qa={ActivityType.start} iconCode={IconCode.musicPlay} onSelect={() => {
                            onSelect(ActivityType.start); addNewActivity(ActivityType.start);
                        }} text={startingText} />}
                        <IconSelectItem qa={ActivityType.normal} iconCode={IconCode.musicPause} onSelect={() => {
                            onSelect(ActivityType.normal); addNewActivity(ActivityType.normal);
                        }} text={commonText} />
                        <IconSelectItem qa={"existujici"} iconCode={IconCode.musicAlbum} onSelect={() => {
                            onSelect("existujici"); setShowExistingActivitiesDiag(true);
                        }} text={existingIntoTheSetText} />
                        <IconSelectItem qa={ActivityType.end} iconCode={IconCode.musicStop} onSelect={() => {
                            onSelect(ActivityType.end); addNewActivity(ActivityType.end);
                        }} text={finalText} />
                    </SelectBody>);
                }}
                selectedOptionContent={({ opened, setOpened }) => <IconButton iconCode={IconCode.docFilePlus} outline={false} onClick={() => setOpened(!opened)} title={addText} backgroundColor={ButtonBackgroundColor.transparent} />}
            />
            {showExistingActivitiesDiag && <ExistingActivitiesDialog addTextLocKey={LOC_ADD} onCancelClick={() => setShowExistingActivitiesDiag(false)} onAddClick={addExistingActivities} {...{ hostKey, selectedSetId, uiWorkflow }} />}
        </Layout>
    );
};

type StateProps = Pick<AddActivityDropdownSelectComponentProps, "selectedSetId" | "scaleIndex" | "addText" | "startingText" | "commonText" | "existingIntoTheSetText" | "finalText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    selectedSetId: getSelectedSetId(state, hostKey),
    scaleIndex: getScaleIndex(getPicassoState(state, hostKey)),
    addText: getPrimaryText(state, LOC_ADD) ?? "Přidat",
    startingText: getPrimaryText(state, LOC_STARTING) ?? "Startovací",
    commonText: getPrimaryText(state, LOC_COMMON) ?? "Běžná",
    existingIntoTheSetText: getPrimaryText(state, LOC_EXISTING_INTO_THE_SET) ?? "Existující do sady",
    finalText: getPrimaryText(state, LOC_FINAL) ?? "Konečná",
});

type DispatchProps = Pick<AddActivityDropdownSelectComponentProps, "updateWorkflowView">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    updateWorkflowView: (setId, selectedActivityId, selectedTransitionId) => dispatch(createUpdateWorkflowViewAction(hostKey, setId, selectedActivityId, selectedTransitionId)),
});

export const AddActivityDropdownSelect = connect(mapStateToProps, mapDispatchToProps)(AddActivityDropdownSelectComponent);
